import { IPoint2d } from '@/editor/math/IPoint2d';
import { OperateAbstract } from './OperateAbstract';
import SeparateWallEntity from '@/editor/entities/separateWallEntity';
import { globalStores } from '@/editor/stores';
import { EEntityType, EWallOperateType } from '@/editor/types';
import VirtualWallModel, { EVirtualWallStatus } from '@/editor/entities/2d/virtualWallModel';
import { WallMoveCalculator } from '@/editor/utils/wall/wallMoveCalculator';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { isModifyWallsLegal } from '@/editor/stores/utils/legality/sepWall';
import { convertSepWallTypeToVirtualWallStyle } from '@/editor/utils/wall';
import { WallPointAbsorbManager } from '@/editor/manager/absorb/wall/wallPointAbsorbManager';
import { LineSeg2d } from '@/editor/math/LineSeg2d';

export class OperateSeparateWall extends OperateAbstract<EEntityType.SeparateWall> {
  private _checkedEntity: SeparateWallEntity | null | undefined = null;
  private _startMove: boolean = false;
  private _startPoint: IPoint2d = { x: 0, y: 0 };
  private _verticalVector: IPoint2d = { x: 0, y: 0 };
  private _offset: number = 0;

  private _virtualWallModel = new VirtualWallModel();
  private _wallPanOperation = new WallMoveCalculator(globalStores.separateWallStore);
  private _wallAbsorbManager = new WallPointAbsorbManager(globalStores.separateWallStore, EEntityType.SeparateWall);

  constructor() {
    super();
  }

  private setCheckedSeparateWallEntity(wallEntity: SeparateWallEntity | undefined | null) {
    if (this._checkedEntity) {
      this._checkedEntity.selected = false;
    }

    this._checkedEntity = wallEntity;

    if (this._checkedEntity) {
      this._checkedEntity.selected = true;
    }
  }

  set checkedEntity(separateWallEntity: SeparateWallEntity | undefined | null) {
    if (separateWallEntity) {
      this.setCheckedSeparateWallEntity(separateWallEntity);
      this._wallAbsorbManager.updateWallData();
    } else {
      this.clear();
    }
  }

  get checkedEntity() {
    return this._checkedEntity;
  }

  override startMove(point: IPoint2d) {
    if (!this._checkedEntity) return;
    const mainWallData = globalStores.separateWallStore.getSepData(this._checkedEntity.id, false);
    if (!mainWallData) return;

    this._wallAbsorbManager.lockSeparateWallRoom(mainWallData.relateRoomId);
    this._wallPanOperation.setMainWall(mainWallData.id);
    const sepSeg = new LineSeg2d(mainWallData.start, mainWallData.end);
    this._startPoint =
      mainWallData.center || sepSeg.pointOnSegment(point) ? point : sepSeg.getFootOfPerpendicular(point);

    const d = Point2dUtils.getUnitDirection(mainWallData.start, mainWallData.end);
    this._verticalVector = Point2dUtils.rotateDirection(d, Math.PI / 2);
  }

  move(point: IPoint2d) {
    if (!this.pickPoint || !this._checkedEntity) return point;
    this._startMove = true;

    const separateWallData = globalStores.separateWallStore.getSepData(this._checkedEntity.id, false);
    if (!separateWallData) return point;

    this.moving = true;

    if (!separateWallData.center) {
      const absorbPoint = this._wallAbsorbManager.calculateAbsorbPointByLineThenRender(
        separateWallData.start,
        separateWallData.end,
        point
      );
      if (absorbPoint) point = absorbPoint;
    }

    const moveVector = Point2dUtils.subtracted(point, this._startPoint);
    this._offset = Point2dUtils.projectScalar(moveVector, this._verticalVector);
    const result = this._wallPanOperation.compute({
      type: EWallOperateType.Pan,
      offset: this._offset,
      direction: this._verticalVector
    });
    const { isLegal } = isModifyWallsLegal({ modifyWallInfos: result?.modifyWallInfos || [] });

    if (!isLegal) {
      this._wallAbsorbManager.clearModel();
    }

    // 更新虚拟墙体
    const baseWallDatas = (
      [...(result?.modifyWallInfos || []), ...(result?.otherRelativeWallInfos || [])] as {
        wallId: string;
        updateBaseData?: { start?: IPoint2d; end?: IPoint2d; center?: IPoint2d; clockwise?: boolean };
      }[]
    )
      .map((info) => {
        const wallData = globalStores.separateWallStore.getSepData(info.wallId, false);
        if (!wallData) return undefined;
        const { start, end, thickness, center, clockwise } = wallData;
        return {
          start: info.updateBaseData?.start || start,
          end: info.updateBaseData?.end || end,
          thickness,
          center: info.updateBaseData?.center || center,
          clockwise: info.updateBaseData?.clockwise ?? clockwise,
          style: convertSepWallTypeToVirtualWallStyle(wallData.type),
          status: isLegal ? EVirtualWallStatus.Normal : EVirtualWallStatus.Unnormal
        };
      })
      .filter((info) => !!info);

    this._virtualWallModel.update(baseWallDatas);

    return point;
  }

  completeMove(_point: IPoint2d) {
    this.moving = false;

    if (!this._checkedEntity || !this._startMove) {
      return;
    }

    const separateWallData = globalStores.separateWallStore.getSepData(this._checkedEntity.id, false);
    if (separateWallData) {
      void globalStores.separateWallOperation.pan(separateWallData.id, this._offset, this._verticalVector);
    }

    this.clear();
    editorEvent.emit(EditorEventName.REFRESH_SELECTED);
  }

  clear() {
    this._startMove = false;
    this._virtualWallModel.clear();
    this._wallAbsorbManager.clearModel();
    this._wallAbsorbManager.clearSeparateWallRoom();
    this.setCheckedSeparateWallEntity(null);
    this._checkedEntity = null;
    this.moving = false;
  }

  dispose() {
    this.clear();

    this._virtualWallModel.dispose();
    this._wallAbsorbManager.dispose();
  }
}
